import { IModalEntity } from '../../../../interfaces/modalEntity';
import { ILanguage } from '../../../../interfaces/language';
import { Modal } from '../../../common/Modal';
import { TEXTS } from '../../../../core/texts';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ValidationErrorState } from '../../../../types/validationErrorState';
import { Validator } from '../../../../core/Validator';
import { ButtonWithLoader } from '../../../common/ButtonWithLoader';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../../services/freelancer';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { FreelancerContext } from '../FreelancerEntity';
import { BaseObj } from '../../../../types/baseObj';
import { SelectLanguageAndProficiency } from '../../../common/SelectLanguageAndProficiency';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../queryHooks/freelancer/useGetFreelancerEntity';
import { useGetLanguageCodes } from '../../../../queryHooks/useGetLanguageCodes';
import { useNotification } from '../../../../+state/useNotification';

const LanguageEntity = ({ onClose, entity }: IModalEntity<ILanguage>) => {
  const { data: codes } = useGetLanguageCodes();
  const { user } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (language: ILanguage) => language.id
      ? FreelancerService.editLanguage(user.id, language as ILanguage & { id: string })
      : FreelancerService.saveLanguage(user.id, language),
    onSuccess: () => {
      openSuccessToast('Successfully saved');
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
      onClose();
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    }
  });

  const codesList = useMemo(() => codes || [], [codes]);
  const [state, setState] = useState<ILanguage>(entity || { languageCode: '', proficiencyLevel: 1 });
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  
  const hasChanges = useMemo(() =>
    !entity || (entity.languageCode !== state.languageCode || entity.proficiencyLevel !== state.proficiencyLevel), [entity, state]);
  
  const handleSubmit = useCallback(() => {
    const validErrors = Validator.validate(state, Validator.NAMES.LANGUAGE);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    mutation.mutate(state);
  }, [state, mutation]);
  
  const handleChange = useCallback((change: BaseObj) => {
    setState(prev => ({...prev, ...change}))
  },[]);
  
  return (
    <Modal
      onClose={onClose} maxWidth={500} title={`${entity?.id ? 'Edit' : 'Add'} ${TEXTS.LANGUAGE}`}
      dialogActions={
        <ButtonWithLoader disabled={!hasChanges} loading={mutation.isLoading}
                          onClick={handleSubmit}>{TEXTS.SAVE}</ButtonWithLoader>
      }
    >
      <SelectLanguageAndProficiency 
        list={codesList} 
        languageCode={state.languageCode}
        proficiencyLevel={state.proficiencyLevel} 
        onChange={handleChange} 
        validationErrors={validationErrors}
        />
    </Modal>
  );
};

export default LanguageEntity;